import useForm from '@/hooks/useForm/useForm';
import useSuGRCities from '@/hooks/useSuGRCities/useSuGRCities';
import useSuGRStreets from '@/hooks/useSuGRStreets/useSuGRStreets';
import { ChangeEvent, useEffect, useRef } from 'react';
import Box from '../Box/Box';
import ButtonSubmit from '../ButtonSubmit/ButtonSubmit';
import { ButtonSubmitState } from '../ButtonSubmit/consts';
import FormField from '../FormField/FormField';
import { FormFieldType } from '../FormField/consts';
import { FormFieldElement } from '../FormField/interfaces';
import FormFieldDropdown from '../FormFieldDropdown/FormFieldDropdown';
import FormFieldSuggestion from '../FormFieldSuggestion/FormFieldSuggestion';
import FormRow from '../FormRow/FormRow';
import styles from './TarriffAddressForm.module.scss';
import { schema } from './schema';
import type { TarriffAddressFormProps, ZipCodeFormBoxState } from './types';

const initialState: ZipCodeFormBoxState = {
  zipCode: '',
  city: '',
  streetName: '',
  streetNumber: '',
};

const AddressTarriffForm = ({
  onSubmit,
  onZipCodeChange,
  submitButtonState,
}: TarriffAddressFormProps) => {
  const {
    formState,
    handleChange,
    handleSubmit,
    getErrorByFieldName,
    handleBlur,
    isFormValid,
    isFieldValid,
    setFormState,
  } = useForm(
    ({ zipCode, city, streetName, streetNumber }) => {
      onSubmit({ zipCode, city, streetName, streetNumber });
    },
    schema,
    initialState
  );

  const useFormFnsRef = useRef({ setFormState, isFieldValid });
  useFormFnsRef.current = { setFormState, isFieldValid };

  const citySuggestions = useSuGRCities(formState.zipCode);

  const isZipCodeValid = isFieldValid('zipCode', true);

  const streetSuggestions = useSuGRStreets(formState.streetName ?? '', {
    city: formState.city,
    zipCode: formState.zipCode,
  });

  const handleZipCodeChange = ($event: ChangeEvent<FormFieldElement>) => {
    if (
      submitButtonState === ButtonSubmitState.Fail &&
      isFieldValid('zipCode', true)
    ) {
      onZipCodeChange?.($event.currentTarget.value);
    }

    handleChange($event);
  };

  useEffect(() => {
    if (!isZipCodeValid || citySuggestions.isError) {
      useFormFnsRef.current.setFormState(() => ({
        ...initialState,
        city: '',
        zipCode: formState.zipCode,
      }));
      return;
    }

    if (isZipCodeValid && citySuggestions.data?.length === 1) {
      const [{ zipCode, city }] = citySuggestions.data;

      useFormFnsRef.current.setFormState(() => ({
        city,
        zipCode,
      }));
    }
  }, [
    citySuggestions.data,
    formState.zipCode,
    citySuggestions.isError,
    citySuggestions.isSuccess,
    isZipCodeValid,
  ]);

  const zipCodeErrorMessage = citySuggestions.isError
    ? 'Keinen Ort gefunden'
    : getErrorByFieldName('zipCode');

  const hasMultipleCities =
    !!citySuggestions.data && citySuggestions.data.length > 1;

  const citySuffixBoxText: string = citySuggestions.isLoading
    ? 'Suchen…'
    : hasMultipleCities
      ? ''
      : formState.city || 'Ort';

  const streetOptions =
    streetSuggestions.data?.map((street) => ({
      label: street,
      value: street,
    })) ?? [];

  return (
    <form onSubmit={handleSubmit} className={styles.base}>
      <Box className={styles.box}>
        <FormRow bottomMarginSize={{ default: 1.5, sm: 2 }}>
          <h3 className={styles.headline}>Wie lautet Ihre Adresse?</h3>
        </FormRow>
        <FormRow
          className={styles.formRow}
          bottomMarginSize={{ default: 1.5, sm: 0.75 }}
        >
          <FormField
            name="zipCode"
            value={formState.zipCode ?? ''}
            type={FormFieldType.Number}
            label="Ihre Postleitzahl"
            autoComplete="postal-code"
            inputMode="numeric"
            maxLength={5}
            onChange={handleZipCodeChange}
            errorMessage={zipCodeErrorMessage}
            onBlur={handleBlur}
            restrictMaxLength
            errorMessageHasRelativePosition
            suffixBoxText={citySuffixBoxText}
            className={styles.field}
          />
        </FormRow>

        {hasMultipleCities && (
          <FormRow
            className={styles.formRow}
            bottomMarginSize={{ default: 1.5, sm: 0.75 }}
          >
            <FormFieldDropdown
              name="city"
              value={formState.city ?? ''}
              label="Wählen Sie Ihren Ort"
              options={citySuggestions.data.map((city) => ({
                label: city.city,
                value: city.city,
              }))}
              onChange={(option) => {
                setFormState((prevState) => ({
                  ...prevState,
                  city: option?.value || '',
                }));
              }}
              errorMessage={getErrorByFieldName('city')}
              errorMessageHasRelativePosition
              className={styles.field}
            />
          </FormRow>
        )}

        <FormRow
          className={styles.formRow}
          bottomMarginSize={{ default: 1.5, sm: 1.5 }}
        >
          <FormFieldSuggestion
            name="streetName"
            value={formState.streetName ?? ''}
            onChange={handleChange}
            errorMessage={getErrorByFieldName('streetName')}
            errorMessageHasRelativePosition
            label="Straße"
            options={streetOptions}
            nativeOptions={streetOptions}
            onSelectOption={(option) => {
              setFormState((prevState) => ({
                ...prevState,
                streetName: option.value,
              }));
            }}
            inputId="streetName"
          />
          <FormField
            label="Haus-Nr."
            name="streetNumber"
            value={formState.streetNumber ?? ''}
            onBlur={handleBlur}
            onChange={handleChange}
            errorMessage={getErrorByFieldName('streetNumber')}
            autoComplete="address-line2"
            halfWidth
            errorMessageHasRelativePosition
          />
        </FormRow>

        <ButtonSubmit
          state={submitButtonState}
          inactive={!isFormValid}
          animate={isFormValid}
          stateMessages={{
            pending: 'Tarife laden…',
            fail: 'Keine Tarife gefunden',
            success: 'Erfolgreich versandt!',
          }}
        >
          Jetzt vergleichen
        </ButtonSubmit>
      </Box>
    </form>
  );
};

export default AddressTarriffForm;
